import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Warning, Info, Error, Help } from '@mui/icons-material';

/**
 * ConfirmDialog - A Material-UI replacement for window.confirm()
 *
 * Usage:
 *   const [confirmOpen, setConfirmOpen] = useState(false);
 *   const [confirmConfig, setConfirmConfig] = useState({});
 *
 *   const handleDelete = () => {
 *     setConfirmConfig({
 *       title: 'Delete Entry',
 *       message: 'Are you sure you want to delete this entry?',
 *       confirmText: 'Delete',
 *       confirmColor: 'error',
 *       onConfirm: () => { deleteEntry(); }
 *     });
 *     setConfirmOpen(true);
 *   };
 *
 *   <ConfirmDialog
 *     open={confirmOpen}
 *     onClose={() => setConfirmOpen(false)}
 *     {...confirmConfig}
 *   />
 */

const iconMap = {
  warning: <Warning color="warning" sx={{ fontSize: 40, mr: 2 }} />,
  error: <Error color="error" sx={{ fontSize: 40, mr: 2 }} />,
  info: <Info color="info" sx={{ fontSize: 40, mr: 2 }} />,
  question: <Help color="primary" sx={{ fontSize: 40, mr: 2 }} />
};

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Confirm',
  message = 'Are you sure?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  severity = 'question', // 'warning', 'error', 'info', 'question'
  showIcon = true
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle
        id="confirm-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          pb: 1
        }}
      >
        {showIcon && iconMap[severity]}
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          size={isMobile ? "medium" : "large"}
          aria-label={cancelText}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          color={confirmColor}
          variant="contained"
          size={isMobile ? "medium" : "large"}
          autoFocus
          aria-label={confirmText}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * useConfirmDialog - Hook for easier confirm dialog management
 *
 * Usage:
 *   const { confirmDialog, showConfirm, ConfirmDialogComponent } = useConfirmDialog();
 *
 *   const handleDelete = async () => {
 *     const confirmed = await showConfirm({
 *       title: 'Delete Entry',
 *       message: 'Are you sure?',
 *       confirmText: 'Delete',
 *       confirmColor: 'error',
 *       severity: 'warning'
 *     });
 *     if (confirmed) {
 *       deleteEntry();
 *     }
 *   };
 *
 *   return (
 *     <>
 *       <Button onClick={handleDelete}>Delete</Button>
 *       <ConfirmDialogComponent />
 *     </>
 *   );
 */
export function useConfirmDialog() {
  const [open, setOpen] = React.useState(false);
  const [config, setConfig] = React.useState({});
  const resolveRef = React.useRef(null);

  const showConfirm = React.useCallback((options) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfig(options);
      setOpen(true);
    });
  }, []);

  const handleClose = React.useCallback(() => {
    setOpen(false);
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
  }, []);

  const handleConfirm = React.useCallback(() => {
    setOpen(false);
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
  }, []);

  const ConfirmDialogComponent = React.useCallback(() => (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      {...config}
    />
  ), [open, config, handleClose, handleConfirm]);

  return {
    showConfirm,
    ConfirmDialogComponent
  };
}
